/**
 * DataTable Component
 * 
 * High-performance data table using TanStack Table with:
 * - Type-aware cell rendering for all Parse types
 * - Column resizing
 * - Sticky header with glassmorphism
 * - Click-to-expand for complex values
 * - Row hover actions
 */

import { useMemo, useState, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { 
  ParseRow, 
  ParseSchema, 
  ParseFieldType, 
  ParsePointer,
  CellRendererProps 
} from '../../../../types/AppPanel';
import { getCellRenderer } from './cells';
import CellDetailModal from './CellDetailModal';

interface DataTableProps {
  /** Data rows to display */
  data: ParseRow[];
  /** Schema with field definitions */
  schema: ParseSchema;
  /** Base URL for dashboard links */
  dashboardUrl?: string;
  /** Callback when pointer is clicked */
  onPointerClick?: (pointer: ParsePointer) => void;
  /** Callback when relation is clicked */
  onRelationClick?: (targetClass: string, sourceObjectId: string, field: string) => void;
  /** Whether table is loading */
  isLoading?: boolean;
  /** Current sort field (server-side controlled) */
  sortField?: string;
  /** Current sort direction (server-side controlled) */
  sortDirection?: 'asc' | 'desc';
  /** Callback when sort changes (triggers server-side fetch) */
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void;
}

export default function DataTable({
  data,
  schema,
  dashboardUrl,
  onPointerClick,
  onRelationClick,
  isLoading,
  sortField,
  sortDirection,
  onSortChange,
}: DataTableProps) {
  // Modal state for viewing full cell content
  const [detailModal, setDetailModal] = useState<{
    isOpen: boolean;
    value: unknown;
    field: string;
    fieldType: ParseFieldType;
  }>({ isOpen: false, value: null, field: '', fieldType: 'String' });

  // Open detail modal
  const openDetail = useCallback((value: unknown, field: string, fieldType: ParseFieldType) => {
    setDetailModal({ isOpen: true, value, field, fieldType });
  }, []);

  // Close detail modal
  const closeDetail = useCallback(() => {
    setDetailModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Build columns from schema
  const columns = useMemo((): ColumnDef<ParseRow>[] => {
    const cols: ColumnDef<ParseRow>[] = [];

    // Always show objectId first
    cols.push({
      id: 'objectId',
      accessorKey: 'objectId',
      header: 'objectId',
      size: 110,
      minSize: 80,
      cell: (info) => (
        <span className="font-mono text-xs text-[#208AEC]">
          {String(info.getValue()).slice(0, 10)}
        </span>
      ),
    });

    // Add schema fields (excluding system fields)
    Object.entries(schema.fields).forEach(([fieldName, fieldDef]) => {
      // Skip system fields
      if (['objectId', 'createdAt', 'updatedAt', 'ACL'].includes(fieldName)) {
        return;
      }

      const fieldType = fieldDef.type as ParseFieldType;

      cols.push({
        id: fieldName,
        accessorKey: fieldName,
        header: fieldName,
        size: getColumnWidth(fieldType),
        minSize: 60,
        cell: (info) => {
          const CellComponent = getCellRenderer(fieldType);
          const cellValue = info.getValue();
          
          const props: CellRendererProps = {
            value: cellValue,
            field: fieldName,
            fieldType,
            targetClass: fieldDef.targetClass,
            row: info.row.original,
            onPointerClick,
            onRelationClick,
            dashboardUrl,
            onExpand: () => openDetail(cellValue, fieldName, fieldType),
          };

          return <CellComponent {...props} />;
        },
      });
    });

    // Add createdAt
    cols.push({
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: 'createdAt',
      size: 140,
      minSize: 100,
      cell: (info) => {
        const DateCell = getCellRenderer('Date');
        return (
          <DateCell
            value={info.getValue()}
            field="createdAt"
            fieldType="Date"
            row={info.row.original}
            onExpand={() => openDetail(info.getValue(), 'createdAt', 'Date')}
          />
        );
      },
    });

    // Add updatedAt
    cols.push({
      id: 'updatedAt',
      accessorKey: 'updatedAt',
      header: 'updatedAt',
      size: 140,
      minSize: 100,
      cell: (info) => {
        const DateCell = getCellRenderer('Date');
        return (
          <DateCell
            value={info.getValue()}
            field="updatedAt"
            fieldType="Date"
            row={info.row.original}
            onExpand={() => openDetail(info.getValue(), 'updatedAt', 'Date')}
          />
        );
      },
    });

    return cols;
  }, [schema, dashboardUrl, onPointerClick, onRelationClick, openDetail]);

  // Create table instance (sorting is server-side, not client-side)
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    // Sorting is controlled externally via sortField/sortDirection props
    // No client-side sorting - data comes pre-sorted from server
  });
  
  // Handle column header click for sorting
  const handleHeaderClick = useCallback((columnId: string) => {
    if (!onSortChange) return;
    
    // Toggle sort direction, or set new field
    if (sortField === columnId) {
      // Same field - toggle direction
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      onSortChange(columnId, newDirection);
    } else {
      // New field - default to descending
      onSortChange(columnId, 'desc');
    }
  }, [onSortChange, sortField, sortDirection]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-3 text-white/50">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Loading data...</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-full text-white/40 text-sm">
        No data in this class
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-auto glass-scroll pb-28 lg:pb-0">
        <table 
          className="w-full text-sm border-collapse"
          style={{ width: table.getCenterTotalSize() }}
        >
          {/* Sticky Header with Server-Side Sorting */}
          <thead className="sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-[#0c121c]/95 backdrop-blur-sm">
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  const isSorted = sortField === columnId;
                  const currentDirection = isSorted ? sortDirection : null;
                  const canSort = !!onSortChange;
                  
                  return (
                    <th
                      key={header.id}
                      className={`relative text-left px-3 py-2.5 text-white/60 font-medium text-xs
                                 border-b border-white/10 whitespace-nowrap select-none uppercase tracking-wide
                                 ${canSort ? 'cursor-pointer hover:bg-white/5 hover:text-white/80' : ''}
                                 transition-colors group`}
                      style={{ width: header.getSize() }}
                      onClick={canSort ? () => handleHeaderClick(columnId) : undefined}
                    >
                      <div className="flex items-center gap-1.5">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        
                        {/* Sort indicator - controlled by props */}
                        {currentDirection === 'asc' && <SortAscIcon />}
                        {currentDirection === 'desc' && <SortDescIcon />}
                        {canSort && !isSorted && (
                          <span className="opacity-0 group-hover:opacity-40 transition-opacity">
                            <SortIcon />
                          </span>
                        )}
                      </div>
                      
                      {/* Resize Handle */}
                      <div
                        onMouseDown={(e) => {
                          e.stopPropagation(); // Prevent sorting when resizing
                          header.getResizeHandler()(e);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          header.getResizeHandler()(e);
                        }}
                        className={`absolute right-0 top-0 h-full w-1 cursor-col-resize 
                                    hover:bg-[#208AEC]/50 transition-colors
                                    ${header.column.getIsResizing() ? 'bg-[#208AEC]' : ''}`}
                      />
                    </th>
                  );
                })}
                {/* Actions column header */}
                <th className="w-10 border-b border-white/10" />
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="hover:bg-white/[0.03] transition-colors group"
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-3 py-2 text-white/80 border-b border-white/5 overflow-hidden"
                    style={{ 
                      width: cell.column.getSize(), 
                      maxWidth: cell.column.getSize() 
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
                {/* Actions */}
                <td className="px-2 border-b border-white/5">
                  {dashboardUrl && (
                    <a
                      href={`${dashboardUrl}/browser/${schema.className}?objectId=${row.original.objectId}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="p-1.5 rounded hover:bg-white/10 text-white/30 
                                 hover:text-white/80 transition-all inline-flex
                                 opacity-0 group-hover:opacity-100"
                      title="Open in Dashboard"
                    >
                      <ExternalLinkIcon />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      <CellDetailModal
        isOpen={detailModal.isOpen}
        value={detailModal.value}
        field={detailModal.field}
        fieldType={detailModal.fieldType}
        onClose={closeDetail}
      />
    </>
  );
}

/** Get default column width based on field type */
function getColumnWidth(type: ParseFieldType): number {
  const widths: Record<ParseFieldType, number> = {
    String: 180,
    Number: 100,
    Boolean: 80,
    Date: 140,
    Pointer: 130,
    Relation: 120,
    Array: 160,
    Object: 180,
    File: 160,
    GeoPoint: 150,
    Polygon: 140,
    ACL: 160,
    Bytes: 110,
  };
  return widths[type] || 150;
}

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" 
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M15 3H21V9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 14L21 3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/** Sort ascending icon */
function SortAscIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-[#208AEC]">
      <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/** Sort descending icon */
function SortDescIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-[#208AEC]">
      <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/** Neutral sort icon (shown on hover when not sorted) */
function SortIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/40">
      <path d="M7 10l5-5 5 5M7 14l5 5 5-5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

